import {
  Component,
  QueryList,
  Renderer2,
  ViewChildren,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  CcyPairLookupService,
  CcyPairPromiseLookupService,
  MarketTimePromiseLookupService,
  MarketTimeLookupService,
  ProfileSettingKeys,
  ActiveUser,
  ClientPromiseLookupService,
  AccountPromiseLookupService,
  ClientAccountVisibilityType,
  BenchmarkFixingPrimaryLookupService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { BfxUserSettingsComponent } from "../bfx-user-settings/bfx-user-settings.component";
import { ICPrivateOrderDefaultsComponent } from "../bfx-element-library/ic-private-order-defaults.component";
import { BfxAmountSpinnerComponent } from "../bfx-interface-controls/bfx-amount-spinner.component";
import { BfxInputControlStateEnum } from "../bfx-interface-controls/models/bfx-interface-controls.enums";
import { BfxClientIntentToggleComponent } from "../bfx-interface-controls/bfx-toggle-client-intent.component";
import { OEApiService } from "../order-entry/services/oe-api.service";
import { BfxOrderEntrySettingsComponent } from "./bfx-order-entry-settings.component";
import { OEDefaultsModel } from "./model/bfx-order-entry-defaults.model";
import { EXCLUDED_TENORS_SPOT_DEFAULTS } from "./model/bfx-settings-constants";
import {
  ICFixingSourceModel,
  ICAutocompleteGenericModel,
  ICFixedCcyModel,
} from "../order-entry/interface/controls/models/bfx-interface-controls.models";
import { ICAutocompleteDefaultsComponent } from "../bfx-element-library/ic-autocomplete-defaults.component";
import { ICCcyPairDefaultsComponent } from "../bfx-element-library/ic-ccy-pair-defaults.component";
import {
  BfxAutoCompleteComponent,
  BfxAutoCompleteComponentState,
} from "../bfx-element-library/bfx-autocomplete.component";
import {
  IBfxUserSettingsPageSubComponent,
  BfxUserSettingsValidityState,
} from "./bfx-user-settings-page.component";
import { BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import { ICFixedCcyComponent } from "../ic-controls/ic-fixed-ccy.component";
import { OESettingsService } from "app/order-entry/services/basic/oe-settings.service";
import { BenchmarkFixingTime } from "shared/cubiq-services/lookup/model/impl/BenchmarkFixingPrimaryLookupService";
import { OENLockedState } from "app/order-entry/data/enums/oe-locked-state.enum";
import { ICNInputControlState } from "../order-entry/interface/controls/models/bfx-interface-controls.enums";

const PROFILE_KEY = ProfileSettingKeys.userOrderEntryBmarkSettings;
const TAB_PROFILE_KEY = ProfileSettingKeys.userOrderEntryActiveTabsSettings;

@Component({
  selector: "bfx-order-entry-settings-bmark",
  templateUrl: "./html/bfx-order-entry-settings-bmark.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderEntrySettingsBmarkComponent
  implements IBfxUserSettingsPageSubComponent<BfxOrderEntrySettingsComponent>, AfterViewInit
{
  /**********************************************************************/
  /* DATA */

  public dataChanged: boolean = false;
  public parentComponent: BfxUserSettingsComponent = null;
  public activeSubComponent: number = 0;
  public categoryComponent: BfxOrderEntrySettingsComponent = null;
  //
  private entryDataToUpdate: string = "";
  private tabDataToUpdate: string = "";
  //
  private fwdEnabled: boolean = false;
  private defaultTenor: CubiqTS.Tenor = CubiqTS.Tenor.SPOT;
  private defaultValueDate: ICAutocompleteGenericModel = null;

  /*---------- Interface References ---------- */
  @ViewChildren("inputClient")
  icClient: QueryList<ICAutocompleteDefaultsComponent>;
  @ViewChildren("inputAccount")
  icAccount: QueryList<ICAutocompleteDefaultsComponent>;
  @ViewChildren("inputCcyPair")
  icCcyPair: QueryList<ICCcyPairDefaultsComponent>;
  @ViewChildren("inputOrderType")
  icOrderType: QueryList<ICAutocompleteDefaultsComponent>;
  @ViewChildren("toggleClientIntent")
  icClientIntent: QueryList<BfxClientIntentToggleComponent>;
  @ViewChildren("toggleFixedCcy") icFixedCcy: QueryList<ICFixedCcyComponent>;
  @ViewChildren("inputOrderAmount")
  icOrderAmount: QueryList<BfxAmountSpinnerComponent>;
  @ViewChildren("inputValueDate")
  icValueDate: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputFixingCode")
  icFixingCode: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputFixing")
  icFixing: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputGoodFrom")
  icGoodFrom: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputGoodUntil")
  icGoodUntil: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputPrivateOrder")
  icPrivateOrder: QueryList<ICPrivateOrderDefaultsComponent>;

  public data: OEDefaultsModel = new OEDefaultsModel();

  public control: {
    showIntentAndAmount: boolean;
    isLoading: boolean;
    isInitializing: boolean;
    clientAccountConstraintAutoSelect: boolean;
  } = {
    showIntentAndAmount: true,
    isLoading: false,
    isInitializing: true,
    clientAccountConstraintAutoSelect: true,
  };

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    private translate: TranslateService,
    private api: OEApiService,
    private currencyPairService: CcyPairPromiseLookupService,
    private marketTimePromiseService: MarketTimePromiseLookupService,
    private clientLookup: ClientPromiseLookupService,
    private accountLookup: AccountPromiseLookupService,
    public renderer2: Renderer2,
    private benchmarkFixingService: BenchmarkFixingPrimaryLookupService,
    private settings: OESettingsService,
    private cd: ChangeDetectorRef,
  ) {
    this.initializeSettings();
    this.initializeLabelTranslations();
    this.initializeDatasets();
  }

  ngAfterViewInit() {
    this.icOrderType.forEach((c) => c.lock());
  }

  /**********************************************************************/
  /* DATASET INITIALIZATION */

  private initializeSettings(): void {
    // client panel
    let financialsSettings = this.api.getSystemConfigForFinancialsPanel();
    this.data.fixingSource.customFixingAllowed = financialsSettings.customFixingAllowed;
    // more
    let moreSettings = this.api.getSystemConfigForMorePanel();
    this.data.privateOrder.show = this.data.privateOrder.showConfig = moreSettings.showPrivateOrder;
    this.fwdEnabled = moreSettings.fwdEnabled;
  }

  private initializeLabelTranslations(): void {
    this.data.currencyPair.label = this.translate.instant(this.data.currencyPair.labelKey);
    this.data.clientIntent.label = this.translate.instant(this.data.clientIntent.labelKey);
    this.data.goodFrom.label = this.translate.instant(this.data.goodFrom.labelKey);
    this.data.goodUntil.label = this.translate.instant(this.data.goodUntil.labelKey);
    this.data.goodFrom.tooltip = this.translate.instant(this.data.goodFrom.tooltipKey);
    this.data.goodUntil.tooltip = this.translate.instant(this.data.goodUntil.tooltipKey);
    this.data.privateOrder.label = this.translate.instant(this.data.privateOrder.labelKey);
    this.data.fixingSource.label = this.translate.instant(this.data.fixingSource.labelKey);
  }

  public initializeDatasets(): void {
    this.initializeOrderTypeDataset();
    this.initializeCurrencyPairs();
    this.initializeFixingSourceDataset();
    this.initializeValueDateDataset();
  }

  private initializeCurrencyPairs(): void {
    let dataset = this.api.getBmarkCcyPairsDataset();
    if (dataset != null && dataset instanceof Array && dataset.length > 0) {
      this.data.currencyPair.dataset = Object.assign([], dataset);
    } else {
      logger.out.error(
        "ERROR: bmarkDefaults.initializeCurrencyPairs(): CcyPairs dataset load failed!",
      );
      this.data.currencyPair.dataset = [];
    }
  }

  private initializeValueDateDataset(): void {
    if (
      this.data.valueDate.dataset == null ||
      (this.data.valueDate.dataset instanceof Array && this.data.valueDate.dataset.length === 0)
    ) {
      let values = Object.values(CubiqTS.Tenor);

      values.forEach((tenorValue: any) => {
        if (typeof tenorValue === "number" && !EXCLUDED_TENORS_SPOT_DEFAULTS.includes(tenorValue)) {
          // if fwdEnabled is off - then we only take SPOT
          if (this.fwdEnabled || CubiqTS.Tenor.SPOT === tenorValue) {
            let tenorModel: ICAutocompleteGenericModel = new ICAutocompleteGenericModel(
              tenorValue,
              this.translate.instant("column.filter.settleDate" + "." + CubiqTS.Tenor[tenorValue]),
            );
            this.data.valueDate.dataset.push(tenorModel);
            if (this.defaultTenor === tenorValue) {
              this.defaultValueDate = tenorModel;
            }
          }
        }
      });
    }
    this.data.valueDate.suggestions = Object.assign(
      new Array<ICAutocompleteGenericModel>(),
      this.data.valueDate.dataset,
    );
  }

  private initializeOrderTypeDataset() {
    this.data.orderType.value = new ICAutocompleteGenericModel(
      CubiqTS.RequestOrderType.BMARK,
      this.settings.getBmarkDesc(),
    );
    this.data.orderType.dataset = [this.data.orderType.value];
    this.data.orderType.disabled = true;
    this.data.orderType.lockState = OENLockedState.LOCKED;
  }

  private initializeFixingSourceDataset(): void {
    this.loadFixingData();
  }

  protected loadFixingData(): void {
    this.data.fixingSource.disabled = false;
    this.data.fixing.disabled = false;

    this.benchmarkFixingService.isPrimed().then(() => {
      this.benchmarkFixingService.getAll().then((bmCodes) => {
        bmCodes.sort((a, b) => a.benchmarkCd.localeCompare(b.benchmarkCd));

        this.data.fixing.allBmarkFixings = bmCodes;
        this.data.fixing.allBmarkCodes = bmCodes.map((b) => b.benchmarkCd);
        this.data.fixing.lockState = OENLockedState.UNLOCKED;
        this.data.fixing.locked = false;
        this.data.fixing.disabled = false;
        this.data.fixing.fixingCodeState = ICNInputControlState.INACTIVE;
        this.data.fixing.fixingState = ICNInputControlState.INACTIVE;

        // edge case where we switch to a client/pair without any fixings
        this.data.fixing.fixings = this.data.fixing.allBmarkCodes;
        this.data.fixing.fixing = null;
        this.data.fixing.fixingCodes = this.data.fixing.allBmarkCodes;
        this.data.fixing.fixingCode = null;

        this.data.fixingSource.dataset = bmCodes.map(
          (bmc) => new ICFixingSourceModel(bmc.benchmarkCd, bmc.benchmarkCd),
        );
        this.data.fixingSource.suggestions = this.data.fixingSource.dataset;

        this.icFixingCode.forEach((c) => c.markForCheck());
        this.icFixing.forEach((c) => c.markForCheck());
      });
    });
  }

  /**********************************************************************/
  /* SETTINGS METHODS */
  /**********************************************************************/

  /* SETTINGS - INITIALIZE */
  public onTabActivated(event: any): void {}

  public initialize(): void {
    let activeUser: ActiveUser = this.parentComponent.activeUser;
    this.activeSubComponent = this.parentComponent.activeSubCategory;
    if (activeUser.profileSettings) {
      if (activeUser.profileSettings.has(PROFILE_KEY)) {
        let savedData = activeUser.profileSettings.get(PROFILE_KEY);
        this.loadData(savedData);
      } else {
        this.control.isInitializing = false;
      }
    } else {
      this.control.isInitializing = false;
    }
  }

  /* SETTINGS - VALIDATE */
  public validate(): Promise<BfxUserSettingsValidityState> {
    return new Promise<BfxUserSettingsValidityState>((resolve) => {
      let state = this.validateData();
      //logger.out.info(`${this.constructor.name}.validate() valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[state]}`);
      resolve(state);
    });
  }

  /* SETTINGS - SAVE */
  public save(request: CubiqTS.ProfileUpdateRequestImpl): void {
    this.saveData(request);
  }

  public postSave(request: CubiqTS.ProfileUpdateRequestImpl): void {
    let activeUser = this.parentComponent.activeUser;
    if (activeUser.profileSettings) {
      activeUser.profileSettings.set(PROFILE_KEY, this.entryDataToUpdate);
      activeUser.profileSettings.set(TAB_PROFILE_KEY, this.tabDataToUpdate);
    }
    this.dataChanged = false;
  }

  /* SETTINGS - RESET */
  public reset(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resetData().then(() => {
        this.cd.detectChanges();
        resolve();
      });
    });
  }

  public postReset(): void {
    this.dataChanged = false;
    this.cd.detectChanges();
  }

  /* SETTINGS - CHANGE */
  public pageDataChanged() {
    if (!this.control.isLoading && !this.control.isInitializing) {
      // register changes after loading is finished
      this.dataChanged = true;
      this.parentComponent.validate(this.categoryComponent);
    }
  }

  public hasDataChanged(): boolean {
    return this.dataChanged;
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {}

  public onDataChanged(event: any, items: Array<any>): void {}

  /**********************************************************************/
  /* SETTINGS METHODS - HELPERS */

  private getProfileSettingFromKey(
    profileSettingKey: string,
    request: CubiqTS.ProfileUpdateRequestImpl,
  ): CubiqTS.ProfileSettingImpl {
    let profileSettings: Array<any> = request.settings;
    if (!profileSettings) return;
    let savedOrderEntryPreferences: CubiqTS.ProfileSettingImpl = profileSettings.find((setting) => {
      return setting.profileSettingKey == profileSettingKey;
    });
    return savedOrderEntryPreferences;
  }

  // Handles Entry Data; Tabs are assumed valid
  private validateData(): BfxUserSettingsValidityState {
    let result: BfxUserSettingsValidityState;
    let checkStateFn = (
      state: ICNInputControlState | BfxInputControlStateEnum | BfxAutoCompleteComponentState,
    ) => {
      if (
        result === BfxUserSettingsValidityState.INVALID ||
        state === BfxInputControlStateEnum.INVALID
      ) {
        result = BfxUserSettingsValidityState.INVALID;
      } else if (
        result === BfxUserSettingsValidityState.VALID ||
        state === BfxInputControlStateEnum.VALID
      ) {
        result = BfxUserSettingsValidityState.VALID;
      } else if (state === BfxInputControlStateEnum.ACTIVE) {
        result = BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE;
      }
    };

    this.icClient.forEach((c) => checkStateFn(c.data.state));
    this.icAccount.forEach((c) => checkStateFn(c.data.state));
    this.icCcyPair.forEach((c) => checkStateFn(c.data.state));
    this.icOrderType.forEach(() => checkStateFn(BfxInputControlStateEnum.VALID));
    this.icOrderType.forEach((c) => c.lock());
    this.icClientIntent.forEach((c) => checkStateFn(c.data.state));
    this.icFixedCcy.forEach(() => checkStateFn(BfxInputControlStateEnum.VALID));
    this.icOrderAmount.forEach((c) => checkStateFn(c.state));
    this.icValueDate.forEach((c) => checkStateFn(c.state));
    this.icFixingCode.forEach((c) => checkStateFn(c.state));
    this.icFixing.forEach((c) => checkStateFn(c.state));
    this.icGoodFrom.forEach((c) => checkStateFn(c.state));
    this.icGoodUntil.forEach((c) => checkStateFn(c.state));

    return result === BfxUserSettingsValidityState.INVALID
      ? result
      : this.dataChanged
        ? BfxUserSettingsValidityState.VALID
        : result;
  }

  // Handles both Entry Data and Tabs
  private saveData(request: CubiqTS.ProfileUpdateRequestImpl): void {
    // Entry Data
    let savedOrderEntryData: CubiqTS.ProfileSettingImpl = this.getProfileSettingFromKey(
      PROFILE_KEY,
      request,
    );
    if (savedOrderEntryData == null) {
      // save as new
      let settingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
      settingObject.profileSettingKey = PROFILE_KEY;
      settingObject.profileSettingValue = this.generateEntryData();
      request.settings.push(settingObject);
    } else {
      // save as update
      savedOrderEntryData.profileSettingValue = this.generateEntryData();
    }
    // TABs
  }
  public resetDataTest(): void {
    this.resetData();
  }

  // Handles both Entry Data and Tabs
  private resetData(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.control.isInitializing = true;
      // ENTRY DATA
      // reset controls
      this.icClient.forEach((c) => c.reset());
      this.icAccount.forEach((c) => c.reset());
      this.icCcyPair.forEach((c) => c.resetInput());
      this.icClientIntent.forEach((c) => c.resetInputControl());
      this.icFixedCcy.forEach((c) => c.reset(false));
      this.icOrderAmount.forEach((c) => c.resetInputControl());
      this.icGoodFrom.forEach((c) => c.reset());
      this.icGoodUntil.forEach((c) => c.reset());
      this.icValueDate.forEach((c) => c.reset());

      this.data.valueDate.value = null;
      this.data.fixingSource.value = null;
      this.data.goodFrom.value = null;
      this.data.goodUntil.value = null;
      this.icPrivateOrder.forEach((c) => c.reset());
      this.data.client.accountConstraint = null;
      // re - initialize
      let activeUser: ActiveUser = this.parentComponent.activeUser;
      if (activeUser.profileSettings && activeUser.profileSettings.has(PROFILE_KEY)) {
        let savedData = activeUser.profileSettings.get(PROFILE_KEY);
        this.loadData(savedData);
      } else {
        this.control.isInitializing = false;
      }
      resolve();
    });
  }

  public generateEntryData(): string {
    let fixingTime =
      this.data.fixing.value instanceof BenchmarkFixingTime
        ? this.data.fixing.value.value + "/" + this.data.fixing.value.timezoneCd
        : null;

    let entryData = {
      client:
        this.data.client.value instanceof CubiqTS.ClientImpl
          ? this.data.client.value.clientId
          : null,
      account:
        this.data.account.value instanceof CubiqTS.AccountImpl ? this.data.account.value.id : null,
      ccyPair:
        typeof this.data.currencyPair.product === "string" &&
        this.data.currencyPair.product.length === 6
          ? this.data.currencyPair.product
          : null,
      clientIntent:
        this.control.showIntentAndAmount && this.data.clientIntent.value != null
          ? this.data.clientIntent.value
          : null,
      ccy:
        this.control.showIntentAndAmount &&
        typeof this.data.currencyPair.product === "string" &&
        this.data.fixedCcy.value instanceof ICFixedCcyModel
          ? this.data.fixedCcy.value.ccy
          : null,
      orderAmount:
        this.data.orderAmount.value instanceof CubiqTS.BigNumber
          ? this.data.orderAmount.value.toFixed(2)
          : null,
      valueDate:
        this.data.valueDate.value instanceof ICAutocompleteGenericModel
          ? this.data.valueDate.value.value
          : null,
      fixingCode:
        this.data.fixingSource.value instanceof ICFixingSourceModel
          ? this.data.fixingSource.value.value
          : null,
      fixingTime: fixingTime,
      privateOrder: this.data.privateOrder.value,
    };
    this.entryDataToUpdate = JSON.stringify(entryData);
    return this.entryDataToUpdate;
  }

  private loadData(savedData: any): void {
    try {
      let parsedData = JSON.parse(savedData);
      this.control.clientAccountConstraintAutoSelect = false;

      if (parsedData != null) {
        this.control.isLoading = true;
        this.control.isInitializing = false;
        let ccyPairSet: boolean = false;

        let promiseArray = new Array<Promise<any>>();
        // client (promise [0])
        if (parsedData.hasOwnProperty("client") && typeof parsedData.client === "string") {
          promiseArray.push(
            this.clientLookup
              .getActiveClient(parsedData.client, ClientAccountVisibilityType.Full)
              .catch((error) => {
                logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                return null;
              }),
          );
        } else {
          // dummy promise to keep up appearances
          promiseArray.push(
            new Promise<null>((resolve) => {
              resolve(null);
            }),
          );
        }
        // account (promise [1])
        if (parsedData.hasOwnProperty("account") && typeof parsedData.account === "string") {
          promiseArray.push(
            this.accountLookup
              .getActiveAccount(parsedData.account, ClientAccountVisibilityType.Full)
              .catch((error) => {
                logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                return null;
              }),
          );
        } else {
          promiseArray.push(
            new Promise<null>((resolve) => {
              resolve(null);
            }),
          );
        }
        // ccyPair
        if (
          parsedData.hasOwnProperty("ccyPair") &&
          typeof parsedData.ccyPair === "string" &&
          parsedData.ccyPair.length === 6
        ) {
          this.data.currencyPair.product = parsedData.ccyPair;
          this.icCcyPair.forEach((c) => c.setCcyPairExternally(parsedData.ccyPair));
          this.onCurrencyPairChange(null);
          ccyPairSet = true;
        } else {
          this.data.valueDate.disabled = true;
        }

        // clientIntent
        if (
          parsedData.hasOwnProperty("clientIntent") &&
          typeof parsedData.clientIntent === "number" &&
          this.icClientIntent != null
        ) {
          this.icClientIntent.forEach((c) => c.writeValue(parsedData.clientIntent));
        }
        // ccy
        if (
          ccyPairSet &&
          parsedData.hasOwnProperty("ccy") &&
          typeof parsedData.ccy === "string" &&
          parsedData.ccy.length === 3
        ) {
          this.icFixedCcy.forEach((c) =>
            c.load(new ICFixedCcyModel(parsedData.ccy, parsedData.ccyPair), false),
          );
          this.data.fixedCcy.ccyPair = parsedData.ccyPair;
          this.data.fixedCcy.fixedCcy = parsedData.ccy;
          this.cd.detectChanges();
        }
        // orderAmount
        if (
          parsedData.hasOwnProperty("orderAmount") &&
          typeof parsedData.orderAmount === "string"
        ) {
          this.data.orderAmount.value = new CubiqTS.BigNumber(parsedData.orderAmount);
        }

        // value date
        if (parsedData.hasOwnProperty("valueDate") && typeof parsedData.valueDate === "number") {
          if (ccyPairSet) {
            let valueDateModel = this.data.valueDate.dataset.find(
              (item) => item.value === parsedData.valueDate,
            );
            if (valueDateModel instanceof ICAutocompleteGenericModel) {
              this.data.valueDate.value = valueDateModel;
            } else {
              logger.out.warn(
                `WARNING: spotDefaults.loadData(): Value Date is present (${parsedData.valueDate}) but the ICAutocompleteGenericModel model is not found in dataset!`,
              );
            }
          } else {
            this.data.valueDate.value = null;
          }
        }

        // fixingCode
        if (parsedData.hasOwnProperty("fixingCode") && typeof parsedData.fixingCode === "string") {
          let fixingSourceModel = this.data.fixingSource.dataset.find(
            (item) => item.value === parsedData.fixingCode,
          );
          if (fixingSourceModel instanceof ICFixingSourceModel) {
            this.data.fixingSource.value = fixingSourceModel;
            this.loadFixingData();
            this.loadFixings();
          } else {
            logger.out.warn(
              `WARNING: bmarkDefaults.loadData(): Fixing code is present (${parsedData.fixingCode}) but the ICFixingSourceModel model is not found in dataset!`,
            );
          }
        }
        // fixingTime
        if (parsedData.hasOwnProperty("fixingTime") && typeof parsedData.fixingCode === "string") {
          let time: string = parsedData.fixingTime;
          let timeZone: string = null;

          if (time.includes("/")) {
            let data: string[] = time.split("/");
            time = data[0];
            timeZone = data[1];
          }

          let fixingSourceModel = this.data.fixing.dataset.find(
            (item) => item.value === time && (timeZone == null || item.timezoneCd === timeZone),
          );
          if (fixingSourceModel instanceof BenchmarkFixingTime) {
            this.data.fixing.value = fixingSourceModel;
          } else {
            logger.out.warn(
              `WARNING: bmarkDefaults.loadData(): Fixing time is present (${parsedData.fixingCode}) but the BenchmarkFixingTime model is not found in dataset!`,
            );
          }
        }

        // privateOrder
        if (
          parsedData.hasOwnProperty("privateOrder") &&
          typeof parsedData.privateOrder === "boolean"
        ) {
          this.data.privateOrder.value = parsedData.privateOrder;
          this.icPrivateOrder.forEach((c) => c.load(parsedData.privateOrder));
        }

        Promise.all(promiseArray).then((values: Array<any>) => {
          // assign values
          let client = values[0];
          let account = values[1];

          // client
          if (client != null && client instanceof CubiqTS.ClientImpl) {
            this.data.client.value = client;
            this.icClient.forEach((c) => c.setDataValue(client, true, false, true, false));
          } else if (parsedData.hasOwnProperty("client") && typeof parsedData.client === "string") {
            logger.out.warn(
              `WARNING: bmarkDefaults.loadData(): Invalid client for '${parsedData.client}'`,
            );
          }
          setTimeout(
            () => {
              // load account after input_constrain & dataset is resolved
              if (account != null && account instanceof CubiqTS.AccountImpl) {
                this.data.account.value = account;
                this.icAccount.forEach((c) => c.setDataValue(account, true, false, true, false));
              } else if (
                parsedData.hasOwnProperty("account") &&
                typeof parsedData.account === "string"
              ) {
                logger.out.warn(
                  `WARNING: bmarkDefaults.loadData(): Invalid client for '${parsedData.client}'`,
                );
              }

              this.initializeValueDateDataset();

              // end loading
              setTimeout(() => {
                this.control.isLoading = false;
                this.control.clientAccountConstraintAutoSelect = true;
                this.parentComponent.validate(this.categoryComponent, true, true).then(() => {
                  this.cd.detectChanges();
                });
              }, 100);
            },
            account == null ? 4 : 100,
          );
        });
      } else {
        this.control.isLoading = false;
        this.control.isInitializing = false;
        this.control.clientAccountConstraintAutoSelect = true;
      }
    } catch (ex) {
      // fallback
      this.control.isLoading = false;
      this.control.isInitializing = false;
      this.control.clientAccountConstraintAutoSelect = true;
    }
  }

  /**********************************************************************/
  /* VALUE CHANGE HANDLERS */

  // Client
  public onClientChange(dataFromInput: any): void {
    this.pageDataChanged();
  }

  public onClientSelect($event: any): void {
    this.pageDataChanged();
  }

  // Account
  public onAccountChange(dataFromInput: any) {
    this.data.account.value = dataFromInput;
    this.pageDataChanged();
  }

  // Handles both account and client
  public onTabChange($event): void {
    this.pageDataChanged();
  }

  // Ccy pair
  public onCurrencyPairChange($event: any): void {
    let productCode: string = this.data.currencyPair.product;
    let productCodeValid: boolean = false;

    if (typeof productCode === "string" && productCode.length === 6) {
      // validate
      productCodeValid =
        this.data.currencyPair.dataset.find(
          (item: string) => item.toLowerCase() === productCode.toLowerCase(),
        ) != null;
      if (productCodeValid) {
        // LOAD CCY MODEL
        let currencyPairLookup: CcyPairLookupService = this.currencyPairService.getBase();
        this.data.currencyPair.product = productCode;
        this.data.currencyPair.model = currencyPairLookup.getItem(productCode);
        this.data.currencyPair.inverted = false;
        // check for invert model
        if (this.data.currencyPair.model == null) {
          this.data.currencyPair.model = currencyPairLookup.getCcyPair(
            productCode.substr(3, 3),
            productCode.substr(0, 3),
          );
          this.data.currencyPair.inverted = this.data.currencyPair.model != null;
        }
        // ALLOCATE BASE / NON-BASE
        if (this.data.currencyPair.inverted) {
          this.data.currencyPair.base = productCode.substr(3, 3);
          this.data.currencyPair.nonBase = productCode.substr(0, 3);
        } else {
          this.data.currencyPair.base = productCode.substr(0, 3);
          this.data.currencyPair.nonBase = productCode.substr(3, 3);
        }

        // PROPAGATE FIXED CCY
        this.data.fixedCcy.ccyPair = productCode;

        // ENABLE VALUE DATE
        this.data.valueDate.disabled = !this.fwdEnabled;
        this.initializeValueDateDataset();

        this.cd.detectChanges();
      }
    }

    // ccyPair invalid or null
    if (!productCodeValid) {
      this.data.valueDate.value = null;
      this.data.valueDate.disabled = true;
    }

    this.pageDataChanged();
  }

  // Order Type
  public onOrderTypeChange($event: any) {
    if (
      this.data.orderType.value instanceof ICAutocompleteGenericModel &&
      this.data.orderType.value.value === CubiqTS.RequestOrderType.CALL
    ) {
      this.control.showIntentAndAmount = false;
    } else {
      this.control.showIntentAndAmount = true;
    }
    this.pageDataChanged();
  }

  // Client Intent
  public onClientIntentToggleChange(dataFromInput: any) {
    this.pageDataChanged();
  }

  // Fixed Ccy
  public onFixedCcyToggleChange(dataFromInput: any) {
    if (
      this.data.fixedCcy.value instanceof ICFixedCcyModel &&
      typeof this.data.fixedCcy.value.ccy === "string"
    ) {
      this.data.fixedCcy.fixedCcy = this.data.fixedCcy.value.ccy;
    } else {
      this.data.fixedCcy.fixedCcy = null;
    }
    this.pageDataChanged();
  }

  // Order Amount
  public onOrderAmountChange(dataFromInput: any) {
    this.pageDataChanged();
  }

  public onOrderAmountBlur(dataFromInput: any) {
    this.pageDataChanged();
  }
  // Fixing Code
  public onFixingCodeBlur(): void {
    this.loadFixings();
    this.pageDataChanged();
  }
  // Value date
  public valueDateOnBlur($event) {
    this.pageDataChanged();
  }
  // Good From
  public onGoodFromChange($event) {
    this.pageDataChanged();
  }
  // Good Until
  public onGoodUntilChange($event) {
    this.pageDataChanged();
  }
  // Private Order
  public onPrivateOrderChange() {
    this.pageDataChanged();
  }

  public onUntilConstraintValidityChange(dataFromInput: any) {}

  /**********************************************************************/
  /* VALUE DATE - METHODS */

  public valueDateSuggestion(event: any) {
    if (event.query != null && typeof event.query === "string" && event.query.length > 0) {
      this.data.valueDate.suggestions = Object.assign(
        new Array<ICAutocompleteGenericModel>(),
        this.data.valueDate.dataset.filter((item) =>
          item.label.toLowerCase().includes(event.query.toLowerCase()),
        ),
      );
    } else {
      this.data.valueDate.suggestions = Object.assign([], this.data.valueDate.dataset);
    }
  }

  /**********************************************************************/
  /* FIXING CODE - METHODS */

  public searchFixingSources(filter: any): void {
    let fixingSources: Array<ICFixingSourceModel> = this.data.fixingSource.dataset;

    let query: string = filter.query.toLowerCase();
    if (query.length > 0) {
      this.data.fixingSource.suggestions = fixingSources.filter((fixingSource: any) =>
        fixingSource.label.toLowerCase().includes(query),
      );
    } else {
      this.data.fixingSource.suggestions = Object.assign([], this.data.fixingSource.dataset);
    }
  }

  public loadFixings(): void {
    if (
      this.data.fixingSource != null &&
      this.data.fixingSource.value instanceof ICFixingSourceModel
    ) {
      let fixingCode: string = this.data.fixingSource.value.value;

      let fixingCodes: CubiqTS.BenchmarkFixingImpl[] = this.data.fixing.allBmarkFixings.filter(
        (b) => b.benchmarkCd == fixingCode,
      );

      if (fixingCodes.length == 1) {
        let fixingCode: CubiqTS.BenchmarkFixingImpl = fixingCodes[0];
        this.data.fixing.dataset = fixingCode.rules.map(
          (r) => new BenchmarkFixingTime(r.localTime, r.timezoneCd),
        );
        this.data.fixing.suggestions = this.data.fixing.dataset;

        if (this.data.fixing.value != null) {
          let matches: boolean =
            this.data.fixing.suggestions.filter((f) => f.value == this.data.fixing.value.value)
              .length > 0;

          if (!matches) {
            this.data.fixing.value = null;
          }
        }
      } else {
        this.data.fixing.dataset = [];
        this.data.fixing.suggestions = this.data.fixing.dataset;
        this.data.fixing.value = null;
      }
    } else {
      this.data.fixing.dataset = [];
      this.data.fixing.suggestions = this.data.fixing.dataset;
      this.data.fixing.value = null;
    }
  }

  public onFixingChange(newFixing): void {
    this.pageDataChanged();
  }

  public searchFixings(filter: any): void {
    let fixings: Array<BenchmarkFixingTime> = this.data.fixing.dataset;

    let query: string = filter.query.toLowerCase();
    if (query.length > 0) {
      this.data.fixing.suggestions = fixings.filter((fixing: any) =>
        fixing.label.toLowerCase().includes(query),
      );
    } else {
      this.data.fixing.suggestions = Object.assign([], this.data.fixing.dataset);
    }
  }
}
